import React from 'react';
import { useTranslation } from 'react-i18next';
import { VotingHistoryItem } from '../../types/history';
import { formatDateTime } from '../../utils/format';

interface LocalHistoryListProps {
  items: VotingHistoryItem[];
  expandedId: string | null;
  onToggle: (id: string) => void;
}

/** Sessions saved to this browser's localStorage, independent of the backend. */
const LocalHistoryList: React.FC<LocalHistoryListProps> = ({ items, expandedId, onToggle }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <div className="no-sessions">
        <p>{t('history.noLocalHistory') || 'まだローカル履歴がありません'}</p>
        <p className="history-hint">{t('history.historyHint') || '投票セッションを完了すると、ここに履歴が保存されます'}</p>
      </div>
    );
  }

  return (
    <div className="sessions-list">
      {items.map((item) => (
        <div key={item.id} className="session-item local-history-item">
          <div className="session-header" onClick={() => onToggle(item.id)}>
            <div className="session-info">
              <h4 className="session-title">{item.storyTitle}</h4>
              <div className="session-meta">
                <span className="session-date">{formatDateTime(item.votedAt)}</span>
                <span className="final-point">{t('history.item.finalPoint') || '最終ポイント'}: {item.finalPoint}</span>
                {item.averagePoint && (
                  <span className="average-point">{t('history.item.averagePoint') || '平均'}: {item.averagePoint.toFixed(1)}</span>
                )}
                <span className="participant-count">
                  {t('history.item.participantCount', { count: item.participants.length }) || `${item.participants.length}人参加`}
                </span>
              </div>
            </div>
            <div className="session-toggle">{expandedId === item.id ? '▼' : '▶'}</div>
          </div>

          {expandedId === item.id && (
            <div className="session-details">
              <div className="local-history-details">
                <div className="participants-info">
                  <strong>{t('history.item.participants') || '参加者'}:</strong>
                  <span className="participants-list">{item.participants.join(', ')}</span>
                </div>

                <div className="votes-details">
                  <strong>{t('history.item.votes') || '投票結果'}:</strong>
                  <div className="votes-grid">
                    {item.votes.map((vote, index) => (
                      <div key={index} className="vote-item">
                        <span className="voter-name">{vote.participant}</span>
                        <span className="vote-value">{vote.point}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="room-info">
                  <small className="room-details">{t('history.item.room') || 'ルーム'}: {item.roomName} (ID: {item.roomId})</small>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default LocalHistoryList;
